import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, MoreVert, Star, StarBorder, Delete } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchProjects,
  deleteProjectThunk,
} from '../../state/slices/projectSlice';
import { updateProjectStarring } from '../../apis/Projects';
import { fetchWorkflows } from '../../state/slices/workflowSlice';
import { fetchFlowExecutionHistoryThunk } from '../../state/slices/dashboardSlice';
import { verifyPasswordForReauth, verifyGoogleReauth, isGoogleUser } from '../../services/reauthService';
import WorkflowTable from './WorkflowTable';
import PropTypes from 'prop-types';
import Folder from '../../Images/Folder.png';

const ProjectsGrid = forwardRef(
  (
    {
      onProjectSelect = () => {},
      limitProjectsToFive = false,
      showTableOnLoad = false,
      showOnlyStarred = false,
      workflowLoading = false,
      starredProjectIds = [],
      setStarredProjectIds = () => {},
      onEdit = () => {},
      onDelete = () => {},
      searchTerm = '',
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const { list: projectsList, loading: projectsLoading } = useSelector(
      (state) => state.projects,
    );
    const { list: workflows, loading: reduxWorkflowLoading } = useSelector(
      (state) => state.workflows,
    );
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuProjectId, setMenuProjectId] = useState(null);
    const borderBase = 'rgba(79, 172, 254, 0.1)';
    
    // Running workflow validation state
    const [runningWorkflowDialogOpen, setRunningWorkflowDialogOpen] = useState(false);
    const [runningWorkflows, setRunningWorkflows] = useState([]);
    const [projectToDelete, setProjectToDelete] = useState(null);
    
    // Reauthentication state for project deletion
    const [reauthDialogOpen, setReauthDialogOpen] = useState(false);
    const [reauthStep, setReauthStep] = useState('confirm'); // 'confirm', 'reauth', 'verify'
    const [reauthPassword, setReauthPassword] = useState('');
    const [showReauthPassword, setShowReauthPassword] = useState(false);
    const [reauthError, setReauthError] = useState('');
    const [reauthLoading, setReauthLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      scrollToTable: () => {
        if (tableRef.current) {
          tableRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      },
    }));

    useEffect(() => {
      if (projectsList.length === 0) {
        dispatch(fetchProjects())
          .unwrap()
          .then((projects) => {
            if (projects.length > 0 && showTableOnLoad) {
              const sorted = [...projects].sort((a, b) => {
                const aTime = new Date(
                  a.updated_at || a.created_at || 0,
                ).getTime();
                const bTime = new Date(
                  b.updated_at || b.created_at || 0,
                ).getTime();
                return bTime - aTime;
              });
              handleProjectSelect(sorted[0], true);
            }
          })
          .catch(() => toast.error('Failed to fetch projects.'));
      } else if (!selectedProject && showTableOnLoad) {
        const sorted = [...projectsList].sort((a, b) => {
          const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
          const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
          return bTime - aTime;
        });
        handleProjectSelect(sorted[0], true);
      }
    }, [dispatch, projectsList, selectedProject, showTableOnLoad]);

    useEffect(() => {
      dispatch(fetchWorkflows()).catch(() =>
        toast.error('Failed to fetch workflows.'),
      );
    }, [dispatch]);

    // Reset reauthentication state when dialog opens/closes
    useEffect(() => {
      if (reauthDialogOpen) {
        setReauthStep('confirm');
        setReauthPassword('');
        setShowReauthPassword(false);
        setReauthError('');
        setReauthLoading(false);
      }
    }, [reauthDialogOpen]);

    const handleProjectSelect = (project, skipScroll = false) => {
      setSelectedProject(project);
      setShowTable(true);
      onProjectSelect(project);

      if (!skipScroll) {
        setTimeout(() => {
          if (tableRef.current) {
            tableRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 100);
      }
    };

    const handleMenuOpen = (event, projectId) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setMenuProjectId(projectId);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setMenuProjectId(null);
    };

    const handleToggleStarred = async () => {
      const isCurrentlyStarred = starredProjectIds?.includes(menuProjectId);
      const newStarredStatus = !isCurrentlyStarred;

      try {
        // Call the API to update starring status
        await updateProjectStarring(menuProjectId, newStarredStatus);

        // Update local state only after successful API call
        if (isCurrentlyStarred) {
          setStarredProjectIds((prev) =>
            prev.filter((id) => id !== menuProjectId),
          );
          toast.info('Removed from Starred', { autoClose: 1500 });
        } else {
          setStarredProjectIds((prev) => [...prev, menuProjectId]);
          toast.success('Marked as Starred', { autoClose: 1500 });
        }
      } catch (error) {
        console.error('Failed to update project starring status:', error);
        toast.error('Failed to update starring status. Please try again.', {
          autoClose: 2000,
        });
      }

      handleMenuClose();
    };

    const checkProjectRunningWorkflows = async (projectId) => {
      try {
        // Get workflows for this project
        const projectWorkflows = workflows.filter(w => w.project_id === projectId);
        
        if (projectWorkflows.length === 0) {
          return []; // No workflows in project
        }
        
        // Get execution history to check for running workflows
        const executionData = await dispatch(fetchFlowExecutionHistoryThunk()).unwrap();
        
        // Check if any workflows are running (ACTIVE status)
        const runningWorkflows = executionData.filter(exec => 
          projectWorkflows.some(w => w.workflow_id === exec.workflow_id) && 
          exec.run_status === 'ACTIVE'
        );
        
        return runningWorkflows;
      } catch (error) {
        console.error('Error checking running workflows:', error);
        return []; // Return empty array on error to allow deletion
      }
    };


    const handleForceDeleteProject = () => {
      if (projectToDelete) {
        dispatch(deleteProjectThunk(projectToDelete.project_id))
          .then(() => {
            // Refresh workflows after project deletion to remove workflows from deleted project
            dispatch(fetchWorkflows());
          });
        toast.success('Project deleted', { autoClose: 1500 });
      }
      setRunningWorkflowDialogOpen(false);
      setRunningWorkflows([]);
      setProjectToDelete(null);
    };

    const handleCancelDeleteProject = () => {
      setRunningWorkflowDialogOpen(false);
      setRunningWorkflows([]);
      setProjectToDelete(null);
    };

    // Reauthentication functions for project deletion
    const handleProjectDeleteClick = async () => {
      const projectToDelete = projectsList.find(
        (p) => p.project_id === menuProjectId
      );
      
      if (!projectToDelete) {
        handleMenuClose();
        return;
      }
      
      // Check for running workflows first
      const runningWorkflows = await checkProjectRunningWorkflows(projectToDelete.project_id);
      
      if (runningWorkflows.length > 0) {
        // Show warning dialog with running workflows
        setRunningWorkflows(runningWorkflows);
        setProjectToDelete(projectToDelete);
        setRunningWorkflowDialogOpen(true);
        handleMenuClose();
      } else {
        // No running workflows, proceed with reauthentication
        setProjectToDelete(projectToDelete);
        setReauthDialogOpen(true);
        handleMenuClose();
      }
    };

    const handleProjectReauth = async () => {
      setReauthLoading(true);
      setReauthError('');

      try {
        if (isGoogleUser()) {
          await handleGoogleReauth();
        } else {
          setReauthStep('reauth');
        }
      } catch (error) {
        setReauthError('Reauthentication failed. Please try again.');
      } finally {
        setReauthLoading(false);
      }
    };

    const handleGoogleReauth = async () => {
      try {
        const result = await verifyGoogleReauth();
        if (result.success) {
          setReauthStep('verify');
          setTimeout(() => {
            performProjectDelete();
          }, 1000);
        } else {
          setReauthError(result.error || 'Google reauthentication failed.');
        }
      } catch (error) {
        setReauthError('Google reauthentication failed. Please try again.');
      }
    };

    const handlePasswordReauth = async () => {
      if (!reauthPassword.trim()) {
        setReauthError('Please enter your password.');
        return;
      }

      setReauthLoading(true);
      setReauthError('');

      try {
        const result = await verifyPasswordForReauth(reauthPassword);
        if (result.success) {
          setReauthStep('verify');
          setTimeout(() => {
            performProjectDelete();
          }, 1000);
        } else {
          setReauthError(result.error || 'Wrong password.');
        }
      } catch (error) {
        setReauthError('Reauthentication failed. Please try again.');
      } finally {
        setReauthLoading(false);
      }
    };

    const performProjectDelete = () => {
      if (projectToDelete) {
        dispatch(deleteProjectThunk(projectToDelete.project_id))
          .then(() => {
            // Refresh workflows after project deletion to remove workflows from deleted project
            dispatch(fetchWorkflows());
          });
        try {
        toast.success('Project deleted', { autoClose: 1500 });
        } catch (error) {
          console.error('Toast error:', error);
        }
      }
      setReauthDialogOpen(false);
      setProjectToDelete(null);
    };

    const getFlowCount = (projectId) => {
      if (reduxWorkflowLoading) return '...';
      return workflows.filter((flow) => flow.project_id === projectId).length;
    };

    // Build base list first
    let baseProjects = showOnlyStarred
      ? projectsList.filter((p) => starredProjectIds?.includes(p.project_id))
      : projectsList;

    // Apply search filtering: match project name OR any workflow name under the project
    const term = (searchTerm || '').trim().toLowerCase();
    let filteredProjects = baseProjects;
    if (term) {
      filteredProjects = baseProjects.filter((project) => {
        const projectNameMatch = project.name?.toLowerCase().includes(term);
        if (projectNameMatch) return true;
        // Check workflows under this project
        const hasMatchingWorkflow = workflows.some(
          (wf) =>
            wf.project_id === project.project_id &&
            (wf.name || '').toLowerCase().includes(term),
        );
        return hasMatchingWorkflow;
      });
    }

    // Sort newest-first by updated_at, then created_at
    const sortedProjects = [...filteredProjects].sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });

    // Finally, limit to five if needed (for Recent)
    let visibleProjects = limitProjectsToFive
      ? sortedProjects.slice(0, 5)
      : sortedProjects;

    // Ensure selection remains valid under filtering
    useEffect(() => {
      if (!term) return; // no change needed when no search
      if (
        selectedProject &&
        !visibleProjects.some(
          (p) => p.project_id === selectedProject.project_id,
        )
      ) {
        // Selected project no longer visible, switch to first visible or hide table
        if (visibleProjects.length > 0) {
          setSelectedProject(visibleProjects[0]);
          setShowTable(true);
        } else {
          setSelectedProject(null);
          setShowTable(false);
        }
      }
    }, [term, projectsList, workflows, showOnlyStarred, limitProjectsToFive]);

    // Workflows to show in the table (filter by search term if provided)
    const getWorkflowsForSelectedProject = () => {
      if (!selectedProject) return [];
      const all = workflows.filter(
        (wf) => wf.project_id === selectedProject.project_id,
      );
      if (!term) return all;
      return all.filter((wf) => (wf.name || '').toLowerCase().includes(term));
    };

    return (
      <>
        {!limitProjectsToFive && (
          <Typography variant="h5" sx={{ mb: 3, fontWeight: '600' }}>
            {showOnlyStarred ? 'Starred Projects' : 'All Projects'}
          </Typography>
        )}

        {showOnlyStarred &&
          visibleProjects.length === 0 &&
          !projectsLoading && (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="h6">No starred projects yet</Typography>
              <Typography>Star a project to see it here.</Typography>
            </Box>
          )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: '1.4rem',
            mx:1,
          }}
        >
          {projectsLoading
            ? [...Array(limitProjectsToFive ? 6 : 8)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  width={'100%'}
                  height={100}
                />
              ))
            : visibleProjects.map((project) => {
                const isSelected =
                  selectedProject?.project_id === project.project_id;
                const isStarred = starredProjectIds?.includes(
                  project.project_id,
                );

                return (
                  <Box
                    key={project.project_id}
                    onClick={() => handleProjectSelect(project)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      backgroundColor: '#ffffff',
                      justifyContent: 'center',
                      py: '10px',
                      px: '5px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: isSelected
                        ? '2px solid #5A66A6'
                        : '1px solid rgba(0, 0, 0, 0.1)',
                      boxShadow: '0 6px 14px rgba(17, 24, 39, 0.12)',
                      transition:
                        'box-shadow .25s ease, transform .2s ease, border-color .25s ease',
                      '&:hover': {
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        boxShadow: '0 6px 14px rgba(17, 24, 39, 0.18)',
                        transform: 'translateY(-1px)',
                        '& .flowBadge': {
                          bgcolor: '#5A66A6',
                          borderColor: '#5A66A6',
                          transform: 'translateY(-2px) scale(1.03)',
                          boxShadow: '0 4px 10px rgba(90,102,166,.35)',
                        },
                        '& .flowBadge .flowText': {
                          color: '#fff',
                        },
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: '0 3px 8px rgba(17, 24, 39, 0.10)',
                      },
                      '&:focus-within .flowBadge': {
                        bgcolor: '#5A66A6',
                        borderColor: '#5A66A6',
                        transform: 'translateY(-2px) scale(1.03)',
                        boxShadow: '0 4px 10px rgba(90,102,166,.35)',
                      },
                    }}
                  >
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, project.project_id)}
                      sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
                    >
                      <MoreVert />
                    </IconButton>

                    <Box
                      sx={{
                        position: 'relative',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={Folder}
                        alt="Project Folder"
                        style={{ width: '40px', height: 'auto' }}
                      />
                      {isStarred && (
                        <Star
                          sx={{
                            position: 'absolute',
                            bottom: '-5px',
                            right: '-5px',
                            color: '#FFD700',
                            fontSize: '1.4rem',
                            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        height: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: '500',
                          fontSize: '0.875rem',
                          textTransform: 'capitalize',
                          letterSpacing: '0.015em',
                          color: '#000',
                          mt: '8px',
                          lineHeight: 1.2,
                        }}
                      >
                        {project.name}
                      </Typography>
                      <Typography
                        // variant="caption"
                        sx={{ color: '#5d5d63ff', mt: '4px', fontSize: '0.75rem' }}
                      >
                        Edited{' '}
                        {new Date(
                          project.updated_at || project.created_at,
                        ).toLocaleDateString()}
                      </Typography>
                      <Box
                        className="flowBadge"
                        sx={{
                          mt: '12px',
                          py: '4px',
                          px: '8px',
                          borderRadius: '20px',
                          display: 'inline-block',
                          border: '1px solid #525252',
                          bgcolor: 'transparent',
                          cursor: 'pointer',
                          transition:
                            'transform .25s cubic-bezier(.22,1,.36,1), background-color .25s ease, color .25s ease, border-color .25s ease, box-shadow .25s ease',
                          willChange: 'transform',
                        }}
                      >
                        <Typography
                          className="flowText"
                          sx={{
                            color: '#5A66A6',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            transition: 'color .2s ease',
                          }}
                        >
                          {getFlowCount(project.project_id)} Flows
                        </Typography>
                      </Box>
                    </Box>

                    {/* Enhanced Menu with same styling as WorkflowTable */}
                    <Menu
                      anchorEl={anchorEl}
                      open={
                        Boolean(anchorEl) &&
                        menuProjectId === project.project_id
                      }
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      sx={{
                        '& .MuiPaper-root': {
                          borderRadius: '12px',
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(79, 172, 254, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                          '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            py: 1.5,
                            px: 2,
                            borderRadius: '8px',
                            mx: 1,
                            my: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: 'rgba(79, 172, 254, 0.1)',
                              transform: 'translateX(4px)',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem onClick={handleToggleStarred}>
                        {isStarred ? (
                          <Star
                            sx={{
                              color: '#fbbf24',
                              fontSize: '1rem',
                              mr: 1.5,
                            }}
                          />
                        ) : (
                          <StarBorder
                            sx={{
                              color: '#64748b',
                              fontSize: '1rem',
                              mr: 1.5,
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#374151',
                            fontWeight: 500,
                          }}
                        >
                          {isStarred ? 'Unstar Project' : 'Star Project'}
                        </Typography>
                      </MenuItem>

                      <MenuItem onClick={handleProjectDeleteClick}>
                        <Delete
                          sx={{
                            color: '#ef4444',
                            fontSize: '1rem',
                            mr: 1.5,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#374151',
                            fontWeight: 500,
                          }}
                        >
                          Delete
                        </Typography>
                      </MenuItem>
                    </Menu>
                  </Box>
                );
              })}
        </Box>

        {showTable && selectedProject && (
          <Box ref={tableRef}>
            <WorkflowTable
              title={selectedProject.name}
              workflows={getWorkflowsForSelectedProject()}
              loading={workflowLoading}
              onEdit={onEdit}
              onDelete={onDelete}
              showCreateButton
              selectedProject={selectedProject}
              showTableOnLoad={false}
            />
          </Box>
        )}

        {/* Running Workflow Warning Dialog */}
        <Dialog
          open={runningWorkflowDialogOpen}
          onClose={handleCancelDeleteProject}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1, color: '#dc2626' }}>
            ⚠️ Cannot Delete Project
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, color: '#374151' }}>
              The project <strong>&quot;{projectToDelete?.name}&quot;</strong> cannot be deleted because it has running workflows.
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
              Please wait for the following workflows to complete before deleting the project:
            </Typography>
            
            <List sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 2 }}>
              {runningWorkflows.map((workflow, index) => (
                <ListItem key={index} sx={{ py: 1, px: 0 }}>
                  <ListItemText
                    primary={workflow.workflow_name}
                    secondary={`Run ID: ${workflow.workflow_run_id}`}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                  />
                  <Chip
                    label="RUNNING"
                    size="small"
                    sx={{
                      bgcolor: '#3B82F6',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                </ListItem>
              ))}
            </List>
            
            <Typography variant="body2" sx={{ mt: 2, color: '#6b7280', fontStyle: 'italic' }}>
              You can check the workflow status in the Flow Execution History page.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleCancelDeleteProject}
              variant="outlined"
              sx={{
                minWidth: 100,
                height: 40,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleForceDeleteProject}
              variant="contained"
              color="error"
              sx={{
                minWidth: 160,
                height: 40,
                borderRadius: 1,
                px: 4,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                fontSize: '0.95rem',
                '&:hover': {
                  boxShadow: '0 8px 20px rgba(220, 38, 38, 0.25)',
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
                transition: 'all .18s ease',
              }}
            >
              Force Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Project Deletion Reauthentication Dialog */}
        <Dialog
  open={reauthDialogOpen}
  onClose={() => setReauthDialogOpen(false)}
  keepMounted
  fullWidth
  PaperProps={{
    elevation: 0,
    sx: {
      borderRadius: 1,
      width: 520,
      overflow: 'hidden',
      backgroundColor: '#ffffff',
      border: `1px solid ${borderBase}`,
      boxShadow: '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 2px rgba(0,0,0,0.06)',
      backdropFilter: 'blur(4px)',
    },
  }}
  BackdropProps={{
    sx: {
      backgroundColor: 'rgba(10, 18, 30, 0.35)',
      backdropFilter: 'blur(4px)',
      '& .MuiDialog-container': { alignItems: 'center' },
    },
  }}
  transitionDuration={{ appear: 250, enter: 250, exit: 200 }}
>
  <DialogTitle sx={{ pb: 1,textAlign: 'center' }}>
    Reauthenticate to Delete Project
  </DialogTitle>

  <DialogContent
    sx={{
      py: 5,
      px: 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      textAlign: 'center',
    }}
  >
    {reauthStep === 'confirm' && (
      <>
        <Typography variant="body1" sx={{ mb: 2, color: '#374151' }}>
          You&apos;re about to delete the project <strong>&quot;{projectToDelete?.name}&quot;</strong>.
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
          This action cannot be undone. Please reauthenticate to confirm.
        </Typography>
      </>
    )}

    {reauthStep === 'reauth' && (
      <>
        <Typography variant="body1" sx={{ mb: 2, color: '#374151' }}>
          Please enter your password to confirm project deletion.
        </Typography>
        <TextField
          fullWidth
          type={showReauthPassword ? 'text' : 'password'}
          value={reauthPassword}
          onChange={(e) => setReauthPassword(e.target.value)}
          placeholder="Enter your password"
          variant="outlined"
          error={!!reauthError}
          helperText={reauthError}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowReauthPassword(!showReauthPassword)}
                  edge="end"
                >
                  {showReauthPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor:'#f8fafc',
              '& fieldset': {
                borderColor: reauthError ? '#ef4444' : borderBase,
              },
              '&:hover fieldset': {
                borderColor: reauthError ? '#ef4444' : 'rgba(79, 172, 254, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: reauthError ? '#ef4444' : '#4facfe',
              },
            },
          }}
        />
      </>
    )}

    {reauthStep === 'verify' && (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" sx={{ color: '#374151' }}>
          Verifying authentication...
        </Typography>
      </Box>
    )}
  </DialogContent>

  <DialogActions sx={{ p: 3, pt: 1 }}>
    <Button
      onClick={() => setReauthDialogOpen(false)}
      variant="text"
      disabled={reauthLoading}
      sx={{
            minWidth: 120,
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            color: '#374151',
          }}
    >
      Cancel
    </Button>

    {reauthStep === 'confirm' && (
      <Button
        onClick={handleProjectReauth}
        variant="contained"
        color="error"
        disabled={reauthLoading}
        sx={{
              minWidth: 140,
              height: 40,
              borderRadius: 1,
              px: 4,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              fontSize: '0.95rem',
              backgroundImage: 'linear-gradient(90deg, #f59e0b, #f97316)',
              color: '#fff',
              '&:hover': {
                backgroundImage: 'linear-gradient(90deg, #f59e0b, #ea580c)',
                boxShadow: '0 8px 20px rgba(79, 172, 254, 0.2)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
              transition: 'all .18s ease',
            }}
      >
        {reauthLoading ? <CircularProgress size={20} /> : 'Reauthenticate'}
      </Button>
    )}

    {reauthStep === 'reauth' && (
      <Button
        onClick={handlePasswordReauth}
        variant="contained"
        color="error"
        disabled={reauthLoading || !reauthPassword.trim()}
        sx={{
              minWidth: 140,
              height: 40,
              borderRadius: 1,
              px: 4,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              fontSize: '0.95rem',
              backgroundImage: 'linear-gradient(90deg, #f59e0b, #f97316)',
              color: '#fff',
              '&:hover': {
                backgroundImage: 'linear-gradient(90deg, #f59e0b, #ea580c)',
                boxShadow: '0 8px 20px rgba(79, 172, 254, 0.2)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
              transition: 'all .18s ease',
            }}
      >
        {reauthLoading ? <CircularProgress size={20} /> : 'Delete Project'}
      </Button>
    )}
  </DialogActions>
</Dialog>
      </>
      
    );
   
  },
);

ProjectsGrid.displayName = 'ProjectsGrid';

ProjectsGrid.propTypes = {
  onProjectSelect: PropTypes.func.isRequired,
  limitProjectsToFive: PropTypes.bool,
  showTableOnLoad: PropTypes.bool,
  workflowLoading: PropTypes.bool,
  showOnlyStarred: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  starredProjectIds: PropTypes.array.isRequired,
  setStarredProjectIds: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
};

export default ProjectsGrid;